/**
 * Interfaz del Repositorio de Puntos de Control (Mapa del Campo)
 *
 * Puntos de control: lugares fijos del campo donde el productor registra
 * observaciones repetidas en el tiempo (condición de pastura, plantas
 * vistas, ambiente, densidad y fotos fechadas) para seguir la evolución.
 *
 * Usa soft-delete (`is_deleted` existe en Supabase): delete() marca
 * is_deleted=true en lugar de borrar la fila, para que la eliminación se
 * propague offline vía UPDATE (mismo modelo que elementos_mapa/lluvias).
 *
 * `fotos` en los eventos viaja como ARRAY de objetos hacia Supabase (JSONB)
 * y se guarda como STRING JSON en los caches locales (SQLite no soporta
 * objetos como valor de columna): el repositorio normaliza en el límite,
 * igual que ElementoMapaRepository hace con `geometria`.
 */

import { IRepository } from './IRepository';
import type {
  AlturaVegetacion,
  Cobertura,
  OrigenAmbiente,
  TipoToma,
} from '../../../helpers/datosModeloPuntoControl';

/** Condición de la pastura observada (4 niveles, pedido del productor). */
export type CondicionPastura = 'mala' | 'regular' | 'buena' | 'excelente';

/** Densidad de la pastura observada (opcional). */
export type DensidadPastura = 'rala' | 'media' | 'densa';

/**
 * Método de medición de la masa seca (ola 2, 2026-10-11: número + método).
 * `marco_asistido` (H1 Pasturas, 2026-10-22): medición con foto cenital +
 * altura y el motor de tablas — distingue de un "marco" a ojo.
 */
export type MetodoMasaSeca = 'canastilla' | 'marco' | 'marco_asistido' | 'estimacion';

export const CONDICIONES_PASTURA: CondicionPastura[] = ['mala', 'regular', 'buena', 'excelente'];
export const DENSIDADES_PASTURA: DensidadPastura[] = ['rala', 'media', 'densa'];
export const METODOS_MASA_SECA: MetodoMasaSeca[] = ['canastilla', 'marco', 'marco_asistido', 'estimacion'];

export const ETIQUETA_CONDICION: Record<CondicionPastura, string> = {
  mala: 'Mala',
  regular: 'Regular',
  buena: 'Buena',
  excelente: 'Excelente'
};

export const ETIQUETA_DENSIDAD: Record<DensidadPastura, string> = {
  rala: 'Rala',
  media: 'Media',
  densa: 'Densa'
};

export const ETIQUETA_METODO_MASA_SECA: Record<MetodoMasaSeca, string> = {
  canastilla: 'Canastilla',
  marco: 'Marco de pastura',
  marco_asistido: 'Marco asistido (foto + altura)',
  estimacion: 'Estimación visual'
};

/**
 * Metadata de una foto de evento. Mientras la foto vive en la cola offline
 * del cliente, `ruta` es `'pendiente:<id de cola>'`; al subir, el servicio
 * la reemplaza por el path real del bucket `puntos-control-media`.
 */
export interface FotoEventoControl {
  /** Path en el bucket o 'pendiente:<id>' mientras sube. */
  ruta: string;
  /** Fecha de TOMA de la foto (YYYY-MM-DD), editable por foto. */
  fecha_toma: string;
  notas?: string | null;
  /** Tipo de ESTA foto ('suelo' para la cenital de la herramienta de medición, aunque la toma
   *  general sea panorámica). Fotos viejas no lo traen: valía el tipo_toma del evento. */
  tipo_toma?: 'cerca' | 'panoramica' | 'suelo' | null;
}

/**
 * Snapshot del cálculo de masa seca (H1 Pasturas, motorPastura.ts): se
 * persiste en el JSONB `calculo_pastura` del evento. Guarda exactamente los
 * coeficientes/factores usados para que el cálculo sea reproducible aunque
 * las tablas del catálogo mejoren después (auditoría del cálculo).
 */
export interface CalculoPasturaSnapshot {
  coef_a: number;
  coef_b: number;
  f_cobertura: number;
  estacion: string;
  fuente_tabla: string;
  version_motor: string;
  /** kg MS/ha antes del factor de cobertura (auditoría del cálculo). */
  kg_ms_ha_sin_cobertura?: number;
  /**
   * Lado del marco físico usado en la foto cenital (cm): 100 (1 m × 1 m) o
   * 25 (25 × 25 cm). No afecta la ecuación por altura; audita el protocolo.
   * Ausente = mediciones anteriores a la opción de marco (→ 1 m × 1 m).
   */
  lado_marco_cm?: number;
}

/**
 * Corte de doble muestreo OPCIONAL (H1 Pasturas): el corte del marco del
 * mismo día anotado a mano. Se guarda como JSONB `corte_ms` del evento; el
 * motor no ajusta nada automáticamente — el dato queda para la calibración
 * futura.
 */
export interface CorteMs {
  /** Peso verde de lo cortado dentro del marco, en gramos. */
  peso_verde_g?: number | null;
  /** Porcentaje de materia seca del corte (0–100). */
  pct_ms?: number | null;
  /** Peso seco del marco (alternativa a pesar verde + %MS), en gramos. */
  peso_seco_g?: number | null;
  /** kg MS/ha "real" del corte (g/m² × 10 según el área del marco). */
  kg_ms_ha?: number | null;
  /** Lado del marco con que se cortó (cm; ausente = 1 m × 1 m). */
  lado_marco_cm?: number | null;
}

/** Estado del análisis de la foto del evento (KAN-280). */
export type EstadoAnalisisFoto = 'local' | 'pendiente_ia' | 'ia_listo' | 'ia_error';

/**
 * Análisis de la foto del evento (JSONB `analisis_foto`). `local` sale de la
 * segmentación por color en el dispositivo (estimación de cobertura vegetal);
 * `ia` lo completa el aiChatBot al haber conexión (cobertura FORRAJERA real).
 */
export interface AnalisisFoto {
  /** Ruta de la foto analizada (puede ser `pendiente:<id>` hasta que suba). */
  foto_ruta: string;
  local: { verde: number; amarillo: number; gris: number; vegetal: number; version: number };
  ia?: {
    cobertura_forrajera_pct: number;
    cobertura_lenosas_pct: number;
    suelo_desnudo_pct: number;
    verde_pct: number;
    amarillo_pct: number;
    gris_pct: number;
    estado: string;
    confianza: 'alta' | 'media' | 'baja';
    motivo?: string;
    modelo: string;
    fecha: string;
  };
  /** Reintentos de la cola de IA ya gastados (tope en el procesador). */
  reintentos?: number;
}

/** Punto de control: lugar fijo del campo con coordenadas. */
export interface PuntoControl {
  id: string;
  empresa_id: string;
  nombre: string;
  /** Longitud (orden GeoJSON). */
  lng: number;
  lat: number;
  /** Ambiente sugerido del catálogo (referencia blanda, nullable). */
  ambiente_clave?: string | null;
  /** Cómo se eligió el ambiente al crear el punto (opcional). */
  origen_ambiente?: OrigenAmbiente | null;
  notas?: string | null;
  creado_por: string;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Observación fechada en un punto de control. */
export interface PuntoControlEvento {
  id: string;
  punto_id: string;
  empresa_id: string;
  /** Fecha de observación (YYYY-MM-DD). Puede haber varios por fecha. */
  fecha_observacion: string;
  condicion_pastura: CondicionPastura;
  densidad?: DensidadPastura | null;
  plantas_observadas?: string | null;
  /** Ambiente observado ese día (puede diferir del sugerido del punto). */
  ambiente_clave?: string | null;
  /** Masa seca medida (kg MS/ha) — opcional (ola 2). */
  masa_seca_kg_ha?: number | null;
  /** Cómo se midió la masa seca; sólo tiene sentido con el número arriba. */
  metodo_masa_seca?: MetodoMasaSeca | null;
  /**
   * Altura estimada de la vegetación en cm sobre la foto cenital (H1
   * Pasturas). Convive con el enum cualitativo `altura_vegetacion` (otro
   * concepto: niveles baja/media/alta de eventos sin medición).
   */
  altura_vegetacion_cm?: number | null;
  /** Clave del catálogo de tablas (TABLA_PASTURA en tablasPastura.ts). */
  tipo_pastura?: string | null;
  /** Snapshot del cálculo del motor (coeficientes usados, auditoría). */
  calculo_pastura?: CalculoPasturaSnapshot | null;
  /** Corte de doble muestreo opcional del mismo día (ver CorteMs). */
  corte_ms?: CorteMs | null;
  /** Análisis de la foto del evento (KAN-280); null = sin análisis. */
  analisis_foto?: AnalisisFoto | null;
  analisis_estado?: EstadoAnalisisFoto | null;
  /** Copia plana de la cobertura forrajera de la IA (0–100) para series y consultas. */
  cobertura_forrajera_pct?: number | null;
  /** Datos opcionales para el modelo de fotos (ver datosModeloPuntoControl). */
  tipo_toma?: TipoToma | null;
  altura_vegetacion?: AlturaVegetacion | null;
  /** Ids de FLORA_GUIA de las plantas que se ven. */
  plantas_checklist?: string[] | null;
  /** Cobertura por categoría (poca/media/mucha). */
  cobertura?: Cobertura | null;
  notas?: string | null;
  /** Metadata de fotos (ver FotoEventoControl). */
  fotos: FotoEventoControl[];
  creado_por: string;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Repositorio de Puntos de Control.
 */
export interface IPuntoControlRepository extends IRepository<PuntoControl, string> {
  /** Puntos de una empresa (excluye soft-deletes), ordenados por nombre. */
  getByEmpresa(empresaId: string): Promise<PuntoControl[]>;
}

/**
 * Repositorio de eventos (observaciones) de puntos de control.
 */
export interface IPuntoControlEventoRepository extends IRepository<PuntoControlEvento, string> {
  /**
   * Eventos de un punto (excluye soft-deletes), MÁS RECIENTES primero
   * (fecha_observacion desc; a igual fecha, created_at desc).
   */
  getByPunto(puntoId: string): Promise<PuntoControlEvento[]>;

  /** Reemplaza el array de fotos de un evento (tras subir una pendiente). */
  updateFotos(id: string, fotos: FotoEventoControl[]): Promise<void>;
}
